/**
 * Notification deep-link routing — SINGLE SOURCE OF TRUTH.
 *
 * Every push/in-app notification carries a standardized payload
 * (`entityType` + `entityId` + `metadata`). This module is the ONE place that
 * decides *which screen* a notification points at; each client app then maps the
 * abstract {@link NotificationScreen} to its own concrete route via an
 * EXHAUSTIVE `Record<NotificationScreen, …>` adapter — so adding a screen here
 * turns any app that forgets to route it into a compile error (no silent drift).
 *
 * INVARIANT: `entityId` je VŽDY skutočné ID `entityType`, na ktoré sa dá prejsť,
 * alebo chýba — NIKDY nie syntetický kľúč dedupu. Dodržiava ho každý volajúci
 * zdieľaného `NotificationDispatcher` (API aj worker); dispatcher ho nekontroluje.
 * Digest notifications (task.overdue/due_today, viewing.reminder_tomorrow) point
 * at the first real entity in the batch; the synthetic per-day grouping key stays
 * an event-level concern and never reaches a client. Digests are recognised by
 * their metadata (`taskIds`, `date`) and routed to a LIST screen — the first
 * entity id is only a fallback for clients without that screen.
 */

/** Abstract destination a notification points at — app-agnostic. */
export type NotificationScreen =
  | 'contact'
  | 'deal'
  | 'viewing'
  | 'calendarDay'
  | 'task'
  | 'taskList'
  | 'property'
  | 'callLog'
  | 'monitor'
  | 'lawyerRequest'
  | 'importJob'
  | 'email'
  | 'photoEdit'

export interface NotificationTarget {
  screen: NotificationScreen
  /** Primary entity id for the screen. Absent → the screen's list/index view. */
  id?: string
  /** Related deal id (commission, lawyer-request) for apps that route to the deal. */
  dealId?: string
  /** Calendar day (YYYY-MM-DD) for screen 'calendarDay'. */
  date?: string
  /** Nehnuteľnosť pre screen 'photoEdit' (editor fotky v jej formulári). */
  propertyId?: string
}

/** The standardized notification fields a client receives (FCM data or in-app row). */
export interface NotificationLinkSource {
  entityType?: string | null
  entityId?: string | null
  metadata?: Record<string, unknown> | null
}

/** `metadata.date` (YYYY-MM-DD) — set only by day-digest notifications. */
function readDate(metadata: Record<string, unknown> | null | undefined): string | undefined {
  const v = metadata?.date
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined
}

/**
 * `metadata.taskIds` — set only by task digests (task.overdue / task.due_today).
 * In-app rows carry the JSON array; FCM data values are strings, so the same
 * array arrives JSON-encoded. Presence (non-empty) is what matters.
 */
function hasTaskDigest(metadata: Record<string, unknown> | null | undefined): boolean {
  const v = metadata?.taskIds
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'string') {
    try {
      const parsed: unknown = JSON.parse(v)
      return Array.isArray(parsed) && parsed.length > 0
    } catch {
      return false
    }
  }
  return false
}

/** `metadata.dealId` arrives as a number (in-app DB row) or string (FCM JSON). */
function readDealId(metadata: Record<string, unknown> | null | undefined): string | undefined {
  const v = metadata?.dealId
  if (typeof v === 'string' && v.length > 0) return v
  if (typeof v === 'number') return String(v)
  return undefined
}

/** `metadata.propertyId` — číslo (in-app riadok) alebo reťazec (FCM JSON). */
function readPropertyId(metadata: Record<string, unknown> | null | undefined): string | undefined {
  const v = metadata?.propertyId
  if (typeof v === 'string' && v.length > 0) return v
  if (typeof v === 'number') return String(v)
  return undefined
}

/**
 * Resolve a notification payload to an abstract navigation target, or `null`
 * when the entity type carries no destination. Clients turn the target into a
 * route via their own exhaustive `Record<NotificationScreen, …>` adapter.
 */
export function resolveNotificationTarget(n: NotificationLinkSource): NotificationTarget | null {
  const id = n.entityId ?? undefined
  const dealId = readDealId(n.metadata)

  switch (n.entityType) {
    case 'contact':
      return { screen: 'contact', id }
    case 'deal':
      return { screen: 'deal', id }
    case 'viewing': {
      // Denny digest (viewing.reminder_tomorrow) nesie metadata.date — cielom
      // je KALENDAR na dany den, nie detail prvej udalosti. entityId (prva
      // udalost dna) ostava ako fallback pre klientov bez kalendaroveho ciela
      // (stary APK bez 'calendarDay' route sa sprava ako doteraz).
      const date = readDate(n.metadata)
      if (date) return { screen: 'calendarDay', date, id }
      return { screen: 'viewing', id }
    }
    case 'task':
      // Denný digest („Po termíne: N úloh" / „N úloh s termínom dnes") nesie
      // metadata.taskIds — cieľom je ZOZNAM úloh, nie detail prvej z nich.
      // entityId (prvá úloha) ostáva ako fallback pre klientov bez zoznamu.
      if (hasTaskDigest(n.metadata)) return { screen: 'taskList', id }
      return { screen: 'task', id }
    case 'property':
      return { screen: 'property', id }
    case 'call_log':
      return { screen: 'callLog' }
    case 'monitor':
      return { screen: 'monitor', id }
    case 'commission':
      // A commission notification's own id has no screen — route to its deal.
      return dealId ? { screen: 'deal', id: dealId } : null
    case 'lawyer-request':
      // Admin has a dedicated legal-request screen (uses `id`); apps without one
      // fall back to the deal (uses `dealId`).
      return { screen: 'lawyerRequest', id, dealId }
    case 'import_job':
      return { screen: 'importJob', id }
    case 'email':
      // entityId = email_messages.id (worker mailbox-sync 'email.received').
      return { screen: 'email', id }
    case 'photo_edit':
      return { screen: 'photoEdit', id, propertyId: readPropertyId(n.metadata) }
    default:
      return null
  }
}
